import type { z } from 'zod'

import type { SenderCountrySelection } from 'Modals/CreateReturnLabelModal/CreateReturnLabel.types'

import type { getReturnCase_returnCaseItem } from 'Constants/graphql/queries/detail/__generated__/getReturnCase'

import type { ReturnCasePortalStatus } from 'Portal/__generated__/globalTypes'

import type { portalReturnLabelModalFormSchema } from './PortalReturnLabelModal.schema'

export enum PortalReturnLabelAddressSourceEnum {
  OFFICE = 'OFFICE',
  CUSTOM = 'CUSTOM',
}

export interface PortalReturnLabelModalSubmitResult {
  trackingReference: string
  organisationId: string
  pdfDownloadUrl: string | null
  /**
   * BE-provided dangerous-goods signal from `ReturnCaseLabelInfo` /
   * `CreateReturnLabelPayload`. When `isDangerousGoods` is true, the success
   * view renders a warning callout with `dangerousGoodsPdf` as the download
   * link. `dangerousGoodsPdf` may be null even when the flag is true if the
   * BE has not produced the PDF yet; the render path falls back to `#`.
   *
   * `isDangerousGoods` is nullable because the refetched
   * `ReturnCaseLabelInfo.isDangerousGoods` is `Boolean` (not `Boolean!`).
   * Letting `null` propagate through the parent helper keeps the modal-side
   * `?? createdReturnLabel.isDangerousGoods` fallback honest when the refetch
   * returns null but the mutation has the truthful value.
   */
  isDangerousGoods: boolean | null
  dangerousGoodsPdf: string | null
}

export interface PortalReturnLabelSelectableReturnCase {
  id: string
  organisationID?: string | null
  portalStatus?: ReturnCasePortalStatus | null
  employee: {
    id: string
    contact: {
      firstName: string | null
      lastName: string | null
    }
  } | null
  stocklistItem: {
    imei: string | null
    imei2: string | null
    serialNumber: string | null
    deviceDefinition: {
      imageURL: string | null
      name: string
      productType: string | null
    } | null
  } | null
}

export interface PortalReturnLabelModalProps {
  returnCase: getReturnCase_returnCaseItem
  senderCountry: SenderCountrySelection
  onClose: () => void
  onSubmit: () => Promise<PortalReturnLabelModalSubmitResult>
}

export interface PortalReturnLabelDeviceIdentificationProps {
  returnCase: PortalReturnLabelSelectableReturnCase
  onRemove?: () => void
}

export interface PortalReturnLabelModalAddDeviceProps {
  excludedReturnCaseIds: string[]
  isAdmin: boolean
  isAtLimit: boolean
  search: (term: string) => Promise<PortalReturnLabelSelectableReturnCase[]>
  onAdd: (rc: PortalReturnLabelSelectableReturnCase) => void
}

export interface PortalReturnLabelModalGhostXButtonProps {
  ariaLabel: string
  onClick: () => void
}

export interface PortalReturnLabelModalSuccessProps {
  onClose: () => void
  result: PortalReturnLabelModalSubmitResult
}

export type PortalReturnLabelModalFormSchema = z.infer<
  typeof portalReturnLabelModalFormSchema
>
